import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ─────────────────────────────────────────────────────────────────────────────
// /kılavuz — Tüm sistemin (kamuya açık sayfalar + admin paneli + öğrenci portalı)
// detaylı kullanım kılavuzu. Şifreler, rotalar, veri modeli ve adım adım akışlar.
// ─────────────────────────────────────────────────────────────────────────────

const SAND = "'Segoe UI', system-ui, sans-serif";

// Sidebar içeriği ile bölüm başlıklarını tek yerden yönetmek için.
const TOC: { id: string; label: string }[] = [
  { id: 'genel', label: '1. Genel Bakış' },
  { id: 'erisim', label: '2. Adresler & Şifreler' },
  { id: 'rotalar', label: '3. Sayfa Haritası (Rotalar)' },
  { id: 'public', label: '4. Kamuya Açık Sayfalar' },
  { id: 'admin', label: '5. Admin Paneli' },
  { id: 'ogrenci', label: '6. Öğrenci Portalı' },
  { id: 'yetki', label: '7. Yetki Matrisi' },
  { id: 'veri', label: '8. Veri Modeli (Firebase)' },
  { id: 'teknik', label: '9. Teknik & Yayınlama' },
  { id: 'sss', label: '10. Sık Sorulanlar' },
];

// ── Küçük yardımcı bileşenler ────────────────────────────────────────────────

const Section: React.FC<{ id: string; eyebrow: string; title: string; children: React.ReactNode; navy?: boolean }> = ({
  id, eyebrow, title, children, navy,
}) => (
  <section
    id={id}
    className={`section ${navy ? 'section-navy' : ''}`}
    style={{ padding: '64px 24px', scrollMarginTop: 90 }}
  >
    <div className="container">
      <span className="section-eyebrow">{eyebrow}</span>
      <h2 className="section-title" style={{ marginBottom: 10 }}>{title}</h2>
      <div className="divider" style={{ marginBottom: 32 }}></div>
      {children}
    </div>
  </section>
);

const Card: React.FC<{ title?: string; children: React.ReactNode; accent?: string }> = ({ title, children, accent = 'var(--gold)' }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e6e9ef',
    borderTop: `3px solid ${accent}`,
    borderRadius: 4,
    padding: '22px 24px',
    boxShadow: '0 1px 3px rgba(11,37,69,0.05)',
  }}>
    {title && (
      <h3 style={{ fontFamily: "'Georgia', serif", color: 'var(--navy)', fontSize: '1.05rem', marginBottom: 12 }}>{title}</h3>
    )}
    {children}
  </div>
);

const P: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <p style={{ fontFamily: SAND, color: 'var(--text-light)', lineHeight: 1.8, fontSize: '0.92rem', marginBottom: 14, ...style }}>
    {children}
  </p>
);

const Ul: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul style={{ fontFamily: SAND, color: 'var(--text-light)', lineHeight: 1.75, fontSize: '0.9rem', paddingLeft: 20, margin: '0 0 6px' }}>
    {items.map((it, i) => <li key={i} style={{ marginBottom: 7 }}>{it}</li>)}
  </ul>
);

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code style={{
    fontFamily: "'SF Mono', Menlo, Consolas, monospace",
    background: 'rgba(11,37,69,0.06)',
    color: 'var(--navy)',
    padding: '2px 7px',
    borderRadius: 3,
    fontSize: '0.83em',
    fontWeight: 600,
  }}>{children}</code>
);

const Pill: React.FC<{ children: React.ReactNode; tone?: 'gold' | 'navy' | 'green' | 'red' }> = ({ children, tone = 'navy' }) => {
  const tones: Record<string, React.CSSProperties> = {
    gold: { background: 'rgba(184,150,46,0.12)', color: '#8a6d1f', border: '1px solid rgba(184,150,46,0.3)' },
    navy: { background: 'rgba(11,37,69,0.08)', color: 'var(--navy)', border: '1px solid rgba(11,37,69,0.18)' },
    green: { background: 'rgba(22,128,72,0.1)', color: '#15803d', border: '1px solid rgba(22,128,72,0.25)' },
    red: { background: 'rgba(190,40,40,0.08)', color: '#b91c1c', border: '1px solid rgba(190,40,40,0.22)' },
  };
  return (
    <span style={{
      fontFamily: SAND, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 20, ...tones[tone],
    }}>{children}</span>
  );
};

const tableWrap: React.CSSProperties = { overflowX: 'auto', border: '1px solid #e6e9ef', borderRadius: 4 };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontFamily: SAND, fontSize: '0.86rem', minWidth: 520 };
const th: React.CSSProperties = { background: 'var(--navy)', color: '#fff', textAlign: 'left', padding: '11px 14px', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' };
const td: React.CSSProperties = { padding: '11px 14px', borderTop: '1px solid #eef0f4', color: 'var(--text-light)', verticalAlign: 'top' };
const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 };

// ─────────────────────────────────────────────────────────────────────────────

const Guide: React.FC = () => (
  <>
    <Navbar />

    {/* Hero */}
    <section className="hero" style={{ padding: '80px 24px', backgroundImage: 'linear-gradient(160deg, rgba(4,15,30,0.96) 0%, rgba(11,37,69,0.92) 100%)' }}>
      <div className="hero-content">
        <span className="hero-eyebrow">Sistem Dokümantasyonu</span>
        <h1>Kullanım Kılavuzu</h1>
        <p>University of American Atlantic Öğrenci Bilgi Sistemi'nin tüm sayfaları, admin paneli, öğrenci portalı, giriş bilgileri ve veri yapısı — tek sayfada, her detayıyla.</p>
      </div>
    </section>

    {/* TOC + İçerik iki kolon */}
    <div style={{ background: 'var(--light)' }}>
      <div className="container" style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 240px) 1fr', gap: 0, alignItems: 'start' }}>
        {/* Sticky İçindekiler */}
        <aside style={{ position: 'sticky', top: 20, padding: '40px 16px 40px 0', alignSelf: 'start' }}>
          <div style={{ fontFamily: SAND, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 14 }}>
            İçindekiler
          </div>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, borderLeft: '2px solid #e0e4ec' }}>
            {TOC.map(t => (
              <a key={t.id} href={`#${t.id}`} style={{
                fontFamily: SAND, fontSize: '0.84rem', color: 'var(--navy)', textDecoration: 'none',
                padding: '7px 14px', marginLeft: -2, borderLeft: '2px solid transparent', lineHeight: 1.4,
              }}>
                {t.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* İçerik kolonu — kendi beyaz arka planı */}
        <div style={{ background: '#fff', minHeight: '50vh' }}>

          {/* 1 — Genel Bakış */}
          <Section id="genel" eyebrow="Başlangıç" title="Genel Bakış">
            <P>
              Bu platform, University of American Atlantic (UAA) için geliştirilmiş bir <strong>tanıtım sitesi</strong> ve
              <strong> öğrenci bilgi sistemi</strong>dir. Üç ana parçadan oluşur:
            </P>
            <div style={grid2}>
              <Card title="Kamuya Açık Site" accent="var(--navy)">
                <P style={{ marginBottom: 0 }}>Ziyaretçilerin gördüğü tanıtım sayfaları: ana sayfa, hakkında, programlar, başvuru ve iletişim formları.</P>
              </Card>
              <Card title="Admin Paneli" accent="var(--gold)">
                <P style={{ marginBottom: 0 }}>Yöneticinin başvuruları, mesajları, dersleri, sınavları, notları ve duyuruları yönettiği şifre korumalı alan.</P>
              </Card>
              <Card title="Öğrenci Portalı" accent="#15803d">
                <P style={{ marginBottom: 0 }}>Öğrencilerin kayıt olup ders aldığı, sınav/not/duyuru gördüğü ve danışmanla mesajlaştığı kişisel alan.</P>
              </Card>
            </div>
            <P style={{ marginTop: 22 }}>
              Tüm veriler <strong>Firebase Realtime Database</strong> üzerinde gerçek zamanlı tutulur; bir tarafta yapılan değişiklik
              (örneğin admin'in girdiği not) diğer tarafta (öğrenci portalı) anında görünür.
            </P>
          </Section>

          {/* 2 — Adresler & Şifreler */}
          <Section id="erisim" eyebrow="Erişim" title="Adresler & Şifreler" navy>
            <div style={grid2}>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderLeft: '3px solid var(--gold)', borderRadius: 4, padding: '20px 22px' }}>
                <div style={{ fontFamily: SAND, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--gold-light)', marginBottom: 10 }}>Canlı Adres</div>
                <a href="https://uiawebsite.web.app" target="_blank" rel="noreferrer" style={{ fontFamily: SAND, color: '#fff', fontSize: '1rem', fontWeight: 600, textDecoration: 'none' }}>https://uiawebsite.web.app</a>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderLeft: '3px solid var(--gold)', borderRadius: 4, padding: '20px 22px' }}>
                <div style={{ fontFamily: SAND, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--gold-light)', marginBottom: 10 }}>Admin Giriş Şifresi</div>
                <div style={{ fontFamily: "'SF Mono', Menlo, monospace", color: '#fff', fontSize: '1rem', fontWeight: 700, letterSpacing: '1px' }}>uaa2025admin</div>
                <div style={{ fontFamily: SAND, color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', marginTop: 8 }}>Adres: <Code>/admin</Code></div>
              </div>
            </div>
            <div style={{ background: 'rgba(184,150,46,0.1)', border: '1px solid rgba(184,150,46,0.3)', borderRadius: 4, padding: '16px 20px', marginTop: 22, fontFamily: SAND, fontSize: '0.86rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
              ⚠️ <strong style={{ color: 'var(--gold-light)' }}>Güvenlik notu:</strong> Admin şifresi şu an istemci kodunda
              (<Code>src/pages/AdminPanel.tsx</Code>) sabit olarak tutulur ve başarılı girişte oturum <Code>sessionStorage</Code> içinde işaretlenir
              (sekme kapanınca düşer). Bu yöntem küçük ekipler için pratiktir ancak gerçek kimlik doğrulama değildir — şifreyi paylaşırken dikkatli olun
              ve gerçek koruma gerekiyorsa Firebase Authentication'a taşınması önerilir.
            </div>
            <P style={{ marginTop: 22, color: 'rgba(255,255,255,0.65)' }}>
              <strong style={{ color: '#fff' }}>Öğrenci girişi</strong> ayrı çalışır: her öğrenci <Code>/student/register</Code> sayfasından kendi
              <strong> e-posta + şifresiyle</strong> hesap oluşturur ve <Code>/student/login</Code> üzerinden giriş yapar. Ortak/sabit bir öğrenci şifresi yoktur.
            </P>
          </Section>

          {/* 3 — Rotalar */}
          <Section id="rotalar" eyebrow="Navigasyon" title="Sayfa Haritası (Rotalar)">
            <P>Site <Code>react-router-dom</Code> ile yönlendirilir. Tüm rotalar <Code>src/App.tsx</Code> içinde tanımlıdır:</P>
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr><th style={th}>Rota</th><th style={th}>Sayfa</th><th style={th}>Erişim</th><th style={th}>Açıklama</th></tr>
                </thead>
                <tbody>
                  {[
                    ['/', 'Home', 'Herkes', 'Tanıtım ana sayfası'],
                    ['/about', 'About', 'Herkes', 'Üniversite hakkında, misyon, RPL, diploma doğrulama'],
                    ['/programs', 'Programs', 'Herkes', 'Akademik programlar listesi'],
                    ['/apply', 'Apply', 'Herkes', 'Online başvuru formu'],
                    ['/contact', 'Contact', 'Herkes', 'İletişim formu'],
                    ['/admin', 'AdminPanel', 'Şifreli', 'Yönetim paneli (uaa2025admin)'],
                    ['/student/register', 'StudentRegister', 'Herkes', 'Yeni öğrenci kaydı'],
                    ['/student/login', 'StudentLogin', 'Herkes', 'Öğrenci girişi'],
                    ['/student/portal', 'StudentPortal', 'Öğrenci', 'Giriş yapan öğrencinin kişisel alanı'],
                    ['/kılavuz', 'Guide', 'Herkes', 'Bu kullanım kılavuzu sayfası'],
                  ].map(([r, p, a, d]) => (
                    <tr key={r}>
                      <td style={td}><Code>{r}</Code></td>
                      <td style={{ ...td, color: 'var(--navy)', fontWeight: 600 }}>{p}</td>
                      <td style={td}>{a === 'Şifreli' ? <Pill tone="gold">Şifreli</Pill> : a === 'Öğrenci' ? <Pill tone="green">Öğrenci</Pill> : <Pill>Herkes</Pill>}</td>
                      <td style={td}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* 4 — Kamuya Açık Sayfalar */}
          <Section id="public" eyebrow="Vitrin" title="Kamuya Açık Sayfalar">
            <div style={grid2}>
              <Card title="Home — /">
                <Ul items={[
                  'Üniversitenin tanıtım hero bölümü ve öne çıkan istatistikler',
                  'Programlara, başvuruya ve iletişime hızlı yönlendirmeler',
                  '“Apply Now” çağrısıyla başvuru formuna geçiş',
                ]} />
              </Card>
              <Card title="About — /about">
                <Ul items={[
                  'Misyon, vizyon ve temel değerler',
                  'RPL (Önceki Öğrenmenin Tanınması) sistemi açıklaması',
                  'USDIS diploma doğrulama platformu bilgisi',
                ]} />
              </Card>
              <Card title="Programs — /programs">
                <Ul items={[
                  'Sunulan akademik programların listesi ve detayları',
                  'Mühendislik, işletme, sosyal ve sağlık bilimleri alanları',
                ]} />
              </Card>
              <Card title="Apply — /apply">
                <Ul items={[
                  <>Başvuru formu: ad soyad, e-posta, telefon, ülke, program, derece, mesaj</>,
                  <>Gönderildiğinde kayıt <Code>applications</Code> düğümüne <Code>status: "new"</Code> ile yazılır</>,
                  'Admin panelindeki “Applications” sekmesinde anında görünür',
                ]} />
              </Card>
              <Card title="Contact — /contact">
                <Ul items={[
                  'İletişim formu: ad soyad, e-posta, telefon, konu, mesaj',
                  <>Mesaj <Code>messages</Code> düğümüne <Code>read: false</Code> ile yazılır</>,
                  'Admin “Messages” sekmesinde okunmamış olarak listelenir',
                ]} />
              </Card>
            </div>
          </Section>

          {/* 5 — Admin Paneli */}
          <Section id="admin" eyebrow="Yönetim" title="Admin Paneli" navy>
            <P style={{ color: 'rgba(255,255,255,0.7)' }}>
              <Code>/admin</Code> adresinde şifre ekranı karşılar. Şifre <Code>uaa2025admin</Code> girilince panel açılır ve sol menüden 8 sekmeye erişilir.
              Oturum, sekme kapanana kadar açık kalır.
            </P>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginTop: 8 }}>
              {[
                { t: 'Dashboard', d: ['Toplam / yeni / kabul edilen başvuru sayıları', 'Okunmamış mesaj, aktif ders ve duyuru sayıları', 'Son başvurular ve son mesajların özeti'] },
                { t: 'Applications (Başvurular)', d: ['Gelen başvurular listelenir; isim/e-posta/programa göre aranır', 'Duruma göre filtre: New / Reviewing / Accepted / Rejected', 'View ile detay açılır; durum değiştirilir veya başvuru silinir'] },
                { t: 'Messages (İletişim)', d: ['İletişim formundan gelen mesajlar; okunmamışlar kalın', 'Open ile açılır, açılınca otomatik “okundu” işaretlenir', 'Reply via Email ile doğrudan e-posta yanıtı verilir'] },
                { t: 'Students (Öğrenciler)', d: ['Kayıtlı tüm öğrenciler sol listede; tıklayınca profil + dersleri açılır', 'Her ders için Not Gir: Vize (0–100), Final (0–100), Harf Notu', 'Harf notu: AA, BA, BB, CB, CC, DC, DD, FF, P, F, I, W → Kaydet'] },
                { t: 'Courses (Dersler)', d: ['+ Yeni Ders Ekle: Kod, Ad, Kredi, Öğretim Üyesi, Program/Saat, Dönem', 'Eklenen dersler tabloda listelenir', 'Sil butonuyla ders kaldırılır'] },
                { t: 'Exams (Sınavlar)', d: ['+ Yeni Sınav Ekle: ders seçimi, tür (Vize/Final/Quiz/Bütünleme)', 'Tarih, saat, yer/salon ve dönem bilgisi', 'Öğrenci yalnızca kayıtlı olduğu dersin sınavını görür'] },
                { t: 'Announcements (Duyurular)', d: ['+ Yeni Duyuru Ekle: başlık, içerik, “Önemli” işareti', 'Önemli duyurular altın renkle vurgulanır', 'Tüm öğrencilere anında görünür; Sil ile kaldırılır'] },
                { t: 'Student Messages (Öğrenci Mesajları)', d: ['Öğrencilerin danışmana yazdığı mesajlar, thread bazlı', 'Okunmamış thread’ler kalın; tıklayınca sohbet açılır', 'Yanıt yazılıp Gönder ile iletilir, öğrencide anında görünür'] },
              ].map(s => (
                <div key={s.t} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: '3px solid var(--gold)', borderRadius: 4, padding: '18px 20px' }}>
                  <h3 style={{ fontFamily: "'Georgia', serif", color: '#fff', fontSize: '1rem', marginBottom: 10 }}>{s.t}</h3>
                  <ul style={{ fontFamily: SAND, color: 'rgba(255,255,255,0.62)', lineHeight: 1.7, fontSize: '0.85rem', paddingLeft: 18, margin: 0 }}>
                    {s.d.map((x, i) => <li key={i} style={{ marginBottom: 6 }}>{x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          {/* 6 — Öğrenci Portalı */}
          <Section id="ogrenci" eyebrow="Öğrenci" title="Öğrenci Portalı">
            <Card title="Kayıt & Giriş" accent="#15803d">
              <Ul items={[
                <><strong>Kayıt</strong> (<Code>/student/register</Code>): Ad Soyad, Öğrenci No, E-posta, Program, Öğrenim Düzeyi (Lisans/Yüksek Lisans/Doktora), Şifre (min. 6 karakter) + Şifre Tekrar. Başarılı kayıtta portal otomatik açılır.</>,
                <><strong>Giriş</strong> (<Code>/student/login</Code>): E-posta + şifre. Zaten giriş yapılmışsa doğrudan portala yönlenir.</>,
              ]} />
            </Card>
            <div style={{ ...grid2, marginTop: 20 }}>
              <Card title="Ana Sayfa (Dashboard)">
                <Ul items={['Kayıtlı ders, yaklaşan sınav sayısı', 'GPA / genel ortalama (notlar girildikçe hesaplanır)', 'Yeni duyuru ve okunmamış mesaj sayısı, bildirimler']} />
              </Card>
              <Card title="Ders İşlemleri">
                <Ul items={['Kayıtlı Derslerim: mevcut dönem dersleri; Kaydı Sil ile çıkış', 'Tüm Dersler: admin’in eklediği dersler; Kayıt Ol ile kaydolma']} />
              </Card>
              <Card title="Sınav İşlemleri">
                <Ul items={['Sadece kayıtlı olunan derslerin sınavları', 'Yaklaşan (bugün+sonrası) ve Geçmiş sınavlar ayrı', 'Ders kodu, ad, tür, tarih, saat, yer gösterilir']} />
              </Card>
              <Card title="Not Durumu">
                <Ul items={['Admin not girince burada görünür', 'GPA banner: ortalama + tamamlanan kredi', 'Transkript: ders bazında vize/final/harf notu (yeşil geçer, kırmızı FF/F)']} />
              </Card>
              <Card title="Duyurular">
                <Ul items={['Admin’in yayınladığı tüm duyurular', 'Önemli olanlar altın kenarlıkla öne çıkar', 'En yeni duyuru en üstte']} />
              </Card>
              <Card title="Mesajlar">
                <Ul items={['Danışmanla birebir sohbet arayüzü', 'Kendi mesajlar sağda (lacivert), admin yanıtları solda (gri)', 'Admin yanıtında menüde Mesajlar (1) bildirimi çıkar']} />
              </Card>
            </div>
          </Section>

          {/* 7 — Yetki Matrisi */}
          <Section id="yetki" eyebrow="Roller" title="Yetki Matrisi">
            <P>Admin ve öğrenci rollerinin hangi işlemleri yapabildiği:</P>
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr><th style={th}>İşlem</th><th style={{ ...th, textAlign: 'center' }}>Admin</th><th style={{ ...th, textAlign: 'center' }}>Öğrenci</th></tr>
                </thead>
                <tbody>
                  {[
                    ['Ders ekle / sil', true, false],
                    ['Derse kayıt ol', false, true],
                    ['Not gir', true, false],
                    ['Notu gör', true, true],
                    ['Duyuru yaz', true, false],
                    ['Duyuru gör', true, true],
                    ['Sınav ekle / sil', true, false],
                    ['Sınav gör', true, 'kendi'],
                    ['Mesaj yanıtla', true, false],
                    ['Mesaj gönder', false, true],
                  ].map(([label, admin, stu], i) => (
                    <tr key={i}>
                      <td style={{ ...td, color: 'var(--navy)', fontWeight: 600 }}>{label as string}</td>
                      <td style={{ ...td, textAlign: 'center', fontSize: '1.05rem' }}>{admin ? '✅' : '—'}</td>
                      <td style={{ ...td, textAlign: 'center', fontSize: '1.05rem' }}>{stu === 'kendi' ? '✅ (kendi dersleri)' : stu ? '✅' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* 8 — Veri Modeli */}
          <Section id="veri" eyebrow="Mimari" title="Veri Modeli (Firebase)" navy>
            <P style={{ color: 'rgba(255,255,255,0.7)' }}>
              Veriler Firebase Realtime Database'de aşağıdaki düğümlerde tutulur. Şema <Code>src/types.ts</Code> içindeki TypeScript tipleriyle eşleşir.
            </P>
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr><th style={th}>Düğüm</th><th style={th}>Tip</th><th style={th}>İçerik</th></tr>
                </thead>
                <tbody>
                  {[
                    ['applications', 'Application', 'Başvuru formu kayıtları (status: new/reviewing/accepted/rejected)'],
                    ['messages', 'ContactMessage', 'İletişim formu mesajları (read bayrağı)'],
                    ['students', 'Student', 'Öğrenci profilleri (uid, studentId, program, degree)'],
                    ['courses', 'Course', 'Dersler (code, name, credits, instructor, schedule, semester)'],
                    ['exams', 'Exam', 'Sınavlar (type: midterm/final/quiz/makeup, date, time, location)'],
                    ['announcements', 'Announcement', 'Duyurular (title, body, important)'],
                    ['grades / enrollments', 'Grade', 'Öğrenci ders notları (midterm, final, letterGrade)'],
                    ['threads / chat', 'MessageThread / ChatMessage', 'Öğrenci–admin mesajlaşması (from, text, read)'],
                  ].map(([n, t, d]) => (
                    <tr key={n}>
                      <td style={{ ...td, color: '#fff' }}><Code>{n}</Code></td>
                      <td style={{ ...td, color: 'var(--gold-light)', fontWeight: 600 }}>{t}</td>
                      <td style={{ ...td, color: 'rgba(255,255,255,0.6)' }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <P style={{ marginTop: 18, color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
              Bağlantı yapılandırması <Code>src/firebase.ts</Code> içindedir (proje: <Code>uiawebsite</Code>, bölge: <Code>europe-west1</Code>).
              Yazma/okuma kuralları <Code>database.rules.json</Code> dosyasında tanımlıdır.
            </P>
          </Section>

          {/* 9 — Teknik & Yayınlama */}
          <Section id="teknik" eyebrow="DevOps" title="Teknik & Yayınlama">
            <div style={grid2}>
              <Card title="Teknoloji">
                <Ul items={[
                  <>React + TypeScript (Create React App)</>,
                  <>Yönlendirme: <Code>react-router-dom</Code></>,
                  <>Backend: Firebase Realtime Database + Auth</>,
                  <>Hosting: Firebase Hosting</>,
                ]} />
              </Card>
              <Card title="Yerel Geliştirme">
                <Ul items={[
                  <><Code>npm install</Code> — bağımlılıkları kur</>,
                  <><Code>npm start</Code> — <Code>localhost:3000</Code>'de çalıştır</>,
                  <><Code>npm run build</Code> — üretim derlemesi</>,
                ]} />
              </Card>
              <Card title="Yayınlama (Deploy)">
                <Ul items={[
                  <><Code>npm run build</Code> ile derle</>,
                  <><Code>firebase deploy</Code> ile yayınla</>,
                  <>Ayarlar: <Code>firebase.json</Code> ve <Code>.firebaserc</Code></>,
                ]} />
              </Card>
              <Card title="Önemli Dosyalar">
                <Ul items={[
                  <><Code>src/App.tsx</Code> — rotalar</>,
                  <><Code>src/firebase.ts</Code> — Firebase bağlantısı</>,
                  <><Code>src/types.ts</Code> — veri tipleri</>,
                  <><Code>src/pages/</Code> — tüm sayfalar</>,
                ]} />
              </Card>
            </div>
          </Section>

          {/* 10 — SSS */}
          <Section id="sss" eyebrow="Yardım" title="Sık Sorulanlar" navy>
            {[
              ['Admin şifresini unuttum / değiştirmek istiyorum.', <>Şifre <Code>src/pages/AdminPanel.tsx</Code> içindeki <Code>ADMIN_PASSWORD</Code> sabitinde tanımlıdır. Değiştirip yeniden derleyip yayınlayın.</>],
              ['Öğrenci giriş yapamıyor.', <>E-posta/şifre doğru mu kontrol edin. Hesabı yoksa önce <Code>/student/register</Code> üzerinden kayıt olmalı. Şifre en az 6 karakter olmalıdır.</>],
              ['Öğrenci sınavı/notu görmüyor.', <>Öğrenci ilgili derse <strong>kayıtlı</strong> olmalı. Sınavlar yalnızca kayıtlı dersler için, notlar ise admin not girdikten sonra görünür.</>],
              ['Başvuru/mesaj panele düşmüyor.', <>Form gönderiminin tamamlandığından ve internet bağlantısının olduğundan emin olun. Veriler Firebase'e yazılır ve panelde gerçek zamanlı görünür.</>],
              ['Bu kılavuza nasıl erişilir?', <><Code>/kılavuz</Code> adresinden herkese açıktır. ASCII uyumluluğu için <Code>/kilavuz</Code> de aynı sayfayı açar.</>],
            ].map(([q, a], i) => (
              <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '16px 0' }}>
                <div style={{ fontFamily: SAND, fontWeight: 700, color: '#fff', fontSize: '0.95rem', marginBottom: 6 }}>{q as string}</div>
                <div style={{ fontFamily: SAND, color: 'rgba(255,255,255,0.62)', fontSize: '0.88rem', lineHeight: 1.7 }}>{a}</div>
              </div>
            ))}
            <div style={{ marginTop: 32, textAlign: 'center' }}>
              <Link to="/contact" className="btn-primary">Daha Fazla Yardım — İletişim</Link>
            </div>
          </Section>

        </div>
      </div>
    </div>

    <Footer />
  </>
);

export default Guide;
